// Maya's face — flat, drawn, and obviously not a photograph.
//
// WHY ILLUSTRATED AND NOT PHOTOREAL
// A realistic face would imply a person is watching, which is untrue, and it is
// the exact pattern disclosure rules are aimed at: EU AI Act Article 50, the
// Illinois AI Video Interview Act, NYC Local Law 144. Maya says out loud that
// she runs the interviews here; a photographic face would work against her own
// disclosure.
//
// A drawn character says "a system with a personality" without claiming to be a
// colleague. It also avoids the uncanny valley, which matters more here than
// anywhere: a near-human face that does not blink quite right unsettles people,
// and this product scores hesitation.
//
// WHY IT IS AN INLINE SVG
// No network request, no asset pipeline, nothing to 404 on a candidate's machine
// mid-interview, and it recolours from the same palette as the aura around it so
// the character reads as one thing. It also scales to any size without a second
// export.
//
// WHAT IT DELIBERATELY DOES NOT DO
// No lip-sync. Mouth shapes that nearly match the audio are worse than none —
// the eye catches the mismatch even when the viewer cannot say what is wrong.
// She blinks, breathes and tilts slightly; the voice carries the rest.

import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";

// Blink timing from life rather than a round number: people blink every 2-8
// seconds, irregularly. A perfectly periodic blink is one of the strongest tells
// that something is animated rather than alive.
function useBlink() {
  const [blinking, setBlinking] = useState(false);

  useEffect(() => {
    let timer;
    const schedule = () => {
      const wait = 2200 + Math.random() * 4200;
      timer = setTimeout(() => {
        setBlinking(true);
        // A real blink closes for roughly 110ms.
        setTimeout(() => setBlinking(false), 110);
        schedule();
      }, wait);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  return blinking;
}

const InterviewerAvatar = ({ size = 132, palette, state = "idle" }) => {
  const blinking = useBlink();
  const speaking = state === "speaking";
  const listening = state === "listening";
  const thinking = state === "thinking";

  // Each mount gets a slightly different sway phase, so two avatars on one
  // screen never move in lockstep.
  const phase = useMemo(() => Math.random() * 4, []);

  const p = palette;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        // Breathing, not bobbing. Two overlapping slow cycles read as alive;
        // one clean sine reads as a loading spinner.
        animation: `mayaSway ${speaking ? 3.4 : 5.2}s ease-in-out infinite`,
        animationDelay: `-${phase}s`,
        "@keyframes mayaSway": {
          "0%,100%": { transform: "translateY(0) rotate(0deg)" },
          "35%": { transform: "translateY(-2.5px) rotate(-.7deg)" },
          "70%": { transform: "translateY(1.5px) rotate(.5deg)" },
        },
      }}
    >
      <svg viewBox="0 0 120 120" width="100%" height="100%" role="img" aria-label="Maya, your interviewer">
        <defs>
          <clipPath id="maya-frame">
            <circle cx="60" cy="60" r="52" />
          </clipPath>
          <linearGradient id="maya-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={p.deep} />
            <stop offset="100%" stopColor={p.deeper} />
          </linearGradient>
          <linearGradient id="maya-hair" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={p.hairLight} />
            <stop offset="100%" stopColor={p.hair} />
          </linearGradient>
        </defs>

        <circle cx="60" cy="60" r="52" fill="url(#maya-bg)" />

        <g clipPath="url(#maya-frame)">
          {/* Shoulders. Cropped by the frame, which is why she reads as a
              portrait rather than a floating head. */}
          <path d="M18 120 C22 96 38 86 60 86 C82 86 98 96 102 120 Z" fill={p.clothes} />
          <path d="M52 86 h16 v8 a8 8 0 0 1 -16 0 Z" fill={p.skinShade} />

          {/* Hair behind */}
          <path
            d="M28 62 C28 36 42 22 60 22 C78 22 92 36 92 62 C92 78 88 92 84 100 C86 78 84 62 80 58 C72 66 48 66 40 58 C36 62 34 78 36 100 C32 92 28 78 28 62 Z"
            fill="url(#maya-hair)"
          />

          {/* Face */}
          <ellipse cx="60" cy="58" rx="21" ry="24" fill={p.skin} />
          <path d="M39 56 a21 24 0 0 1 42 0 v-4 a21 20 0 0 0 -42 0 Z" fill={p.skinShade} opacity=".35" />

          {/* Fringe, sitting over the face so the hairline is not a hard edge */}
          <path
            d="M38 52 C40 34 50 26 60 26 C70 26 80 34 82 52 C76 40 68 36 60 36 C52 36 44 40 38 52 Z"
            fill="url(#maya-hair)"
          />

          {/* Brows. Raised a touch while asking, which is what turns a face
              from neutral into interested. */}
          <g
            style={{
              transform: `translateY(${speaking || listening ? -0.8 : 0}px)`,
              transition: "transform .4s ease",
            }}
          >
            <path d="M48 51 q4.5 -2.6 9 -0.4" stroke={p.line} strokeWidth="1.8" fill="none" strokeLinecap="round" />
            <path d="M63 50.6 q4.5 -2.2 9 0.4" stroke={p.line} strokeWidth="1.8" fill="none" strokeLinecap="round" />
          </g>

          {/* Eyes. Scaled to nothing on a blink rather than swapped for a line:
              one element, and the closing reads as a lid rather than a cut. */}
          <g
            style={{
              transformOrigin: "60px 58px",
              transform: `scaleY(${blinking ? 0.08 : 1})`,
              transition: "transform .06s ease",
            }}
          >
            <ellipse cx="52.5" cy="58" rx="3.1" ry="3.6" fill={p.line} />
            <ellipse cx="67.5" cy="58" rx="3.1" ry="3.6" fill={p.line} />
            <circle cx="53.6" cy="56.8" r="1" fill="#ffffff" opacity=".9" />
            <circle cx="68.6" cy="56.8" r="1" fill="#ffffff" opacity=".9" />
          </g>

          {/* Mouth. Three fixed shapes, never lip-sync — a mouth that nearly
              matches the audio is worse than one that plainly does not try. */}
          {speaking ? (
            <ellipse cx="60" cy="70.5" rx="4.2" ry="3" fill={p.line} opacity=".85">
              <animate
                attributeName="ry"
                values="1.6;3.4;2.1;3.6;1.8"
                dur="0.62s"
                repeatCount="indefinite"
              />
            </ellipse>
          ) : thinking ? (
            <path d="M55 70.5 q5 1.6 10 -0.6" stroke={p.line} strokeWidth="1.8" fill="none" strokeLinecap="round" />
          ) : (
            <path d="M54.5 69.5 q5.5 4 11 0" stroke={p.line} strokeWidth="1.8" fill="none" strokeLinecap="round" />
          )}

          {/* A little warmth on the cheeks. Without it a flat face reads clinical. */}
          <ellipse cx="45" cy="65" rx="4" ry="2.6" fill={p.blush} opacity=".5" />
          <ellipse cx="75" cy="65" rx="4" ry="2.6" fill={p.blush} opacity=".5" />
        </g>

        {/* Frame ring, tying her to the aura around her */}
        <circle cx="60" cy="60" r="52" fill="none" stroke={p.ring} strokeWidth="1.5" opacity=".7" />

        {/* Listening indicator: three dots that rise with the candidate's voice
            live in the aura, not here — a face that reacts to your volume is
            unsettling. This is just a quiet "I am attending to you". */}
        {thinking && (
          <g fill={p.ring}>
            {[0, 1, 2].map((i) => (
              <circle key={i} cx={50 + i * 10} cy={106} r="2.6">
                <animate
                  attributeName="opacity"
                  values="0.25;1;0.25"
                  dur="1.2s"
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>
        )}
      </svg>
    </Box>
  );
};

export default InterviewerAvatar;
